import {
  useEffect,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { usePostHog } from "@posthog/react";
import {
  firestoreKioskDirectiveSource,
  type KioskDirectiveSource,
  type Rollout,
} from "../api/app-config";
import { getIsIdle, subscribeActivity } from "../shared/lib/kiosk-activity";

// Drives remote reloads of the kiosk from the app_config/{env} directive.
// Mounted by the app-shell recovery host whenever a validated kiosk binding is
// available. It deliberately lives outside the kiosk runtime error boundary.
//
// Two independent paths share one evaluation:
//   - version path: targetVersion != runningVersion  (loop-guarded)
//   - command path: command.id != lastCommandId      (one-shot kick)
// Soft reloads wait for an idle gap between customers; hard reloads fire now.

const RELOAD_FLUSH_DELAY_MS = 600;
const MAX_VERSION_ATTEMPTS = 2;
const GUARD_KEY = "kiosk_update_guard";
const LAST_COMMAND_KEY = "kiosk_last_command_id";

const runningVersion = (import.meta.env.VITE_APP_VERSION as string) || "";

type Guard = { version: string; count: number };

function readGuard(): Guard | null {
  try {
    const raw = localStorage.getItem(GUARD_KEY);
    return raw ? (JSON.parse(raw) as Guard) : null;
  } catch {
    return null;
  }
}

function writeGuard(g: Guard) {
  try {
    localStorage.setItem(GUARD_KEY, JSON.stringify(g));
  } catch {
    /* storage unavailable (Safari private mode) — best effort */
  }
}

function clearGuard() {
  try {
    localStorage.removeItem(GUARD_KEY);
  } catch {
    /* ignore */
  }
}

function getLastCommandId(): string | null {
  try {
    return localStorage.getItem(LAST_COMMAND_KEY);
  } catch {
    return null;
  }
}

function setLastCommandId(id: string) {
  try {
    localStorage.setItem(LAST_COMMAND_KEY, id);
  } catch {
    /* ignore */
  }
}

function rolloutMatches(
  rollout: Rollout,
  storeKey: string,
  zoneId: string,
): boolean {
  const storeOk = rollout.stores === "all" || rollout.stores.includes(storeKey);
  const zoneOk = rollout.zones === "all" || rollout.zones.includes(zoneId);
  return storeOk && zoneOk;
}

function scheduleElapsed(scheduledAtMs: number | null): boolean {
  return scheduledAtMs == null || Date.now() >= scheduledAtMs;
}

function parseSemver(v: string): [number, number, number] | null {
  const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(v);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

// Prod/semver only: returns false unless BOTH versions parse as semver, so a
// staging commit SHA never triggers a min-version hard-kill.
function isBelowMinVersion(running: string, min: string | null): boolean {
  if (!min) return false;
  const r = parseSemver(running);
  const m = parseSemver(min);
  if (!r || !m) return false;
  for (let i = 0; i < 3; i++) {
    if (r[i] < m[i]) return true;
    if (r[i] > m[i]) return false;
  }
  return false;
}

export function KioskUpdateController({
  storeKey,
  zoneId,
  directiveSource = firestoreKioskDirectiveSource,
}: {
  storeKey: string;
  zoneId: string;
  directiveSource?: KioskDirectiveSource;
}) {
  const posthog = usePostHog();

  const directive = useSyncExternalStore(
    directiveSource.subscribe,
    directiveSource.getSnapshot,
  );

  const isIdle = useSyncExternalStore(subscribeActivity, getIsIdle);

  // Once we commit to a reload, stop evaluating so isIdle/snapshot changes in
  // the 600ms flush window can't schedule a second reload.
  const reloadScheduledRef = useRef(false);
  const pendingReportedRef = useRef<string | null>(null);
  const desyncReportedRef = useRef<string | null>(null);
  const [showDesync, setShowDesync] = useState(false);
  // Wakes the effect at a scheduled deadline (an idle kiosk has no other event
  // to re-trigger evaluation when scheduledAt passes).
  const [scheduleTick, bumpScheduleTick] = useReducer((n: number) => n + 1, 0);
  const scheduleTimerRef = useRef<number | null>(null);

  const baseProps = {
    store_key: storeKey,
    zone_id: zoneId,
    running_version: runningVersion,
  };

  function triggerReload(extra: Record<string, unknown>) {
    reloadScheduledRef.current = true;
    posthog.capture("kiosk_update_reload", { ...baseProps, ...extra });
    const cacheBust = String(extra.target_version ?? extra.command_id ?? "");
    // Delay so the PostHog event flushes; cache-bust param guarantees a fresh
    // index.html even if a CDN/header edge case would otherwise serve a stale
    // entry document. replace() avoids polluting kiosk history.
    window.setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set("_v", cacheBust);
      window.location.replace(url.toString());
    }, RELOAD_FLUSH_DELAY_MS);
  }

  function reportPendingOnce(source: string, extra: Record<string, unknown>) {
    if (pendingReportedRef.current === source) return;
    pendingReportedRef.current = source;
    posthog.capture("kiosk_update_pending", { ...baseProps, source, ...extra });
  }

  useEffect(() => {
    if (!directive) return;
    if (reloadScheduledRef.current) return;

    // Reached the target build → clear the loop guard and report success once.
    if (
      directive.targetVersion &&
      runningVersion === directive.targetVersion &&
      readGuard()
    ) {
      posthog.capture("kiosk_update_success", {
        ...baseProps,
        target_version: directive.targetVersion,
      });
      clearGuard();
      desyncReportedRef.current = null;
      setShowDesync(false);
    }

    const commandApplies =
      directive.command != null &&
      directive.command.id !== getLastCommandId() &&
      rolloutMatches(directive.command.rollout, storeKey, zoneId);

    // --- Version path (requires knowing our own running version) ---
    const versionTargetApplies =
      runningVersion !== "" &&
      directive.targetVersion != null &&
      directive.targetVersion !== runningVersion &&
      rolloutMatches(directive.rollout, storeKey, zoneId);

    if (versionTargetApplies) {
      const target = directive.targetVersion as string;
      // minVersion is an emergency kill switch for known-bad builds; it must
      // bypass a future schedule, otherwise the bad build keeps running until
      // the scheduled time.
      const belowMin = isBelowMinVersion(runningVersion, directive.minVersion);

      if (!scheduleElapsed(directive.scheduledAtMs) && !belowMin) {
        // Scheduled for later: arm a timer to re-evaluate at the deadline, then
        // fall through so a command kick can still fire in the meantime.
        const delay = Math.max(
          0,
          (directive.scheduledAtMs as number) - Date.now(),
        );
        scheduleTimerRef.current = window.setTimeout(bumpScheduleTick, delay);
      } else {
        const guard = readGuard();
        const attempts = guard?.version === target ? guard.count : 0;

        if (attempts >= MAX_VERSION_ATTEMPTS) {
          // Stop reloading — it won't help (locked deploy / CDN lag). Do NOT
          // return: fall through so a command kick can still recover this
          // kiosk while it's stuck in desync.
          if (desyncReportedRef.current !== target) {
            desyncReportedRef.current = target;
            setShowDesync(true);
            posthog.capture("kiosk_update_desync", {
              ...baseProps,
              target_version: target,
              attempts,
            });
          }
        } else {
          const hard = directive.mode === "hard" || belowMin;
          if (hard || isIdle) {
            writeGuard({ version: target, count: attempts + 1 });
            // A version reload supersedes a pending command — mark it executed
            // so it doesn't re-fire after the reload lands.
            if (commandApplies) setLastCommandId(directive.command!.id);
            triggerReload({
              source: "version",
              target_version: target,
              mode: hard ? "hard" : "soft",
              attempt: attempts + 1,
            });
          } else {
            reportPendingOnce("version", { target_version: target });
          }
        }
      }
    }

    // --- Command path (version-independent; skipped only when a version reload
    // is already firing, since the page is about to navigate away) ---
    if (!reloadScheduledRef.current && commandApplies) {
      const cmd = directive.command!;
      if (cmd.action === "hardReload" || isIdle) {
        setLastCommandId(cmd.id);
        triggerReload({
          source: "command",
          command_id: cmd.id,
          action: cmd.action,
        });
      } else {
        reportPendingOnce("command", { command_id: cmd.id });
      }
    }

    return () => {
      if (scheduleTimerRef.current != null) {
        clearTimeout(scheduleTimerRef.current);
        scheduleTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directive, isIdle, storeKey, zoneId, scheduleTick]);

  if (!showDesync) return null;

  // Staff-visible indicator: the kiosk could not converge on the target build
  // after repeated reloads and needs manual attention.
  return (
    <div
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        zIndex: 9999,
        background: "#b91c1c",
        color: "#fff",
        font: "600 12px/1.2 system-ui, sans-serif",
        padding: "6px 10px",
        borderRadius: 8,
        pointerEvents: "none",
      }}
    >
      Kiosk update pending — please restart the app
    </div>
  );
}
